'use client';

import { useEffect, useMemo, useRef, useState } from 'react';
import { FiTrash2, FiPlus, FiMove } from 'react-icons/fi';

import {
  getStrengthCategories,
  getStrengthExercisesByCategory,
  isValidStrengthCategory,
} from './strengthAppendix';
import { Dropdown } from '@/app/components/ui/Dropdown';
import { HiCheck, HiOutlineChevronDown } from 'react-icons/hi';
import SegmentedToggle from '@/app/components/ui/SegmentedToggle';

import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  parseStrengthWorkoutRowText,
  StrengthWorkoutStep,
} from '@trainme/api/client';
import {
  getDefaultStrengthWeightUnit,
  readStrengthWeightUnitCookie,
  unitToLabel,
  writeStrengthWeightUnitCookie,
} from './strengthWeightUnit';

type StrengthRow = StrengthWorkoutStep & {
  id: string;
};

const DEFAULT_ROW_BASE: Omit<StrengthRow, 'id'> = {
  repeatCount: 1,
  exerciseCategory: 'CHEST',
  exerciseName: 'BENCH_PRESS',
  reps: 12,
  weightValue: 25,
  weightDisplayUnit: 'POUND',
  restDurationSeconds: 60,
};

const formatCategoryLabel = (value: string) =>
  value
    .toLowerCase()
    .split(' ')
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');

const REST_OPTIONS: Array<{ label: string; value: number }> = [
  { label: '15 sec', value: 15 },
  { label: '30 sec', value: 30 },
  { label: '45 sec', value: 45 },
  { label: '1 min', value: 60 },
  { label: '2 min', value: 120 },
  { label: '3 min', value: 180 },
  { label: '4 min', value: 240 },
  { label: '5 min', value: 300 },
  { label: '10 min', value: 600 },
];

const formatDuration = (seconds: number) =>
  seconds % 60 === 0 ? `${Math.max(1, seconds / 60)} min` : `${seconds}s`;

const formatWeight = (
  weightValue: number,
  unit: StrengthRow['weightDisplayUnit']
) => `${weightValue} ${unitToLabel(unit)}`;

const getInitialStrengthWeightUnit = () =>
  readStrengthWeightUnitCookie() ?? getDefaultStrengthWeightUnit();

const formatRowText = (row: StrengthRow) =>
  `${row.repeatCount}x(${row.exerciseCategory[0] ?? ''}${row.exerciseCategory.slice(1).toLowerCase()}: ${row.exerciseName} x ${row.reps} @ ${formatWeight(row.weightValue, row.weightDisplayUnit)} + rest ${formatDuration(row.restDurationSeconds)})`;

const parseRows = (text: string): Omit<StrengthRow, 'id'>[] =>
  text
    .split('\n')
    .map((line) => parseStrengthWorkoutRowText(line.trim()))
    .filter((row): row is Omit<StrengthRow, 'id'> => Boolean(row));

const renderRows = (rows: StrengthRow[]) => rows.map(formatRowText).join('\n');

const createRowDrafts = (text: string) => {
  const parsed = parseRows(text);
  return parsed.length > 0
    ? parsed.map((row, index) => ({ ...row, id: `row-${index}` }))
    : [
        {
          ...DEFAULT_ROW_BASE,
          weightDisplayUnit: getInitialStrengthWeightUnit(),
          id: 'row-0',
        },
      ];
};

const createDefaultText = () =>
  `1x(Chest: Bench Press x 12 @ 25 ${unitToLabel(
    getInitialStrengthWeightUnit()
  )} + rest 1 min)`;

function CompactDropdown({
  value,
  options,
  onChange,
  className,
}: {
  value: string;
  options: Array<{ value: string; label: string }>;
  onChange: (next: string) => void;
  className?: string;
}) {
  const selectedLabel =
    options.find((option) => option.value === value)?.label ?? value;

  return (
    <Dropdown
      placement='bottom-start'
      matchTriggerWidth={false}
      menuClassName='w-max min-w-max bg-white text-gray-900 dark:bg-stone-700 dark:text-gray-100'
      trigger={
        <button
          type='button'
          className={[
            'flex w-full items-center justify-between gap-1.5 rounded-lg border border-gray-300 px-1.5 py-0.5 text-left text-[0.72rem]',
            'bg-stone-200 text-gray-900 hover:bg-stone-100 dark:border-gray-700 dark:bg-stone-600 dark:text-white dark:hover:bg-stone-500',
            className ?? '',
          ].join(' ')}
        >
          <span className='min-w-0 truncate'>{selectedLabel}</span>
          <HiOutlineChevronDown className='h-3.5 w-3.5 shrink-0 opacity-70' />
        </button>
      }
    >
      {options.map((option) => (
        <Dropdown.Item
          key={option.value}
          onClick={() => onChange(option.value)}
          active={option.value === value}
          className='rounded-none hover:bg-stone-100 focus:bg-stone-100 dark:hover:bg-stone-600/70 dark:focus:bg-stone-600/70'
        >
          <span className='flex w-full items-center justify-between gap-2'>
            <span className='min-w-0 truncate'>{option.label}</span>
            {option.value === value && (
              <HiCheck className='h-4 w-4 shrink-0 opacity-80' />
            )}
          </span>
        </Dropdown.Item>
      ))}
    </Dropdown>
  );
}

// Draggable item component
function SortableRow({
  row,
  index,
  updateRow,
  removeDraftRow,
  categories,
  isDragging,
}: {
  row: StrengthRow;
  index: number;
  updateRow: (index: number, patch: Partial<StrengthRow>) => void;
  removeDraftRow: (index: number) => void;
  categories: string[];
  isDragging: boolean;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isOver,
    setActivatorNodeRef,
  } = useSortable({ id: row.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    backgroundColor: isOver ? '#e2e8f0' : '',
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className='flex flex-nowrap items-center justify-between gap-1 rounded-xl p-0 group min-w-full'
    >
      {/* Drag Handle */}
      <div
        ref={setActivatorNodeRef}
        {...attributes}
        {...listeners}
        className='flex w-7 cursor-grab items-center justify-center p-0.5 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300 active:cursor-grabbing'
      >
        <FiMove size={14} />
      </div>

      {/* Row Content */}
      <div className='flex w-20 shrink-0 grow-0 flex-col gap-1 text-xs font-medium'>
        <input
          type='number'
          step='1'
          className='w-full rounded-lg border border-gray-300 px-2 py-0.5 text-xs dark:border-gray-700 dark:bg-stone-600'
          inputMode='numeric'
          value={row.repeatCount}
          onChange={(e) =>
            updateRow(index, {
              repeatCount: Number(e.target.value || 1),
            })
          }
        />
      </div>
      <div className='flex min-w-0 flex-1 flex-col text-xs font-medium'>
        <CompactDropdown
          value={row.exerciseCategory}
          options={categories.map((category) => ({
            value: category,
            label: formatCategoryLabel(category),
          }))}
          onChange={(nextCategory) => {
            updateRow(index, { exerciseCategory: nextCategory });
            if (isValidStrengthCategory(nextCategory)) {
              updateRow(index, {
                exerciseName:
                  getStrengthExercisesByCategory(nextCategory)[0]?.label ??
                  row.exerciseName ??
                  DEFAULT_ROW_BASE.exerciseName,
              });
            }
          }}
        />
      </div>
      <div className='flex min-w-0 flex-1 flex-col gap-1 text-xs font-medium'>
        <CompactDropdown
          value={row.exerciseName}
          options={getStrengthExercisesByCategory(row.exerciseCategory).map(
            (exercise) => ({
              value: exercise.value,
              label: exercise.label,
            })
          )}
          onChange={(nextExercise) =>
            updateRow(index, { exerciseName: nextExercise })
          }
        />
      </div>
      <div className='flex w-20 shrink-0 grow-0 flex-col gap-1 text-xs font-medium'>
        <input
          type='number'
          step='1'
          className='w-full rounded-lg border border-gray-300 px-2 py-0.5 text-xs dark:border-gray-700 dark:bg-stone-600'
          inputMode='numeric'
          value={row.reps}
          onChange={(e) =>
            updateRow(index, {
              reps: Number(e.target.value || 1),
            })
          }
        />
      </div>
      <div className='flex w-20 shrink-0 grow-0 flex-col gap-1 text-xs font-medium'>
        <input
          type='number'
          step='2.5'
          className='w-full rounded-lg border border-gray-300 px-2 py-0.5 text-xs dark:border-gray-700 dark:bg-stone-600'
          value={row.weightValue}
          onChange={(e) =>
            updateRow(index, {
              weightValue: Number(e.target.value || 25),
            })
          }
        />
      </div>
      <div className='flex w-24 shrink-0 grow-0 flex-col gap-1 text-xs font-medium'>
        <CompactDropdown
          value={row.weightDisplayUnit}
          className='px-1'
          options={[
            { value: 'POUND', label: 'Pound' },
            { value: 'KILOGRAM', label: 'Kilogram' },
          ]}
          onChange={(nextUnit) =>
            updateRow(index, {
              weightDisplayUnit: nextUnit as StrengthRow['weightDisplayUnit'],
            })
          }
        />
      </div>
      <div className='flex w-20 shrink-0 grow-0 flex-col gap-1 text-xs font-medium'>
        <CompactDropdown
          value={String(row.restDurationSeconds)}
          options={REST_OPTIONS.map((option) => ({
            value: String(option.value),
            label: option.label,
          }))}
          onChange={(nextRest) =>
            updateRow(index, {
              restDurationSeconds: Number(nextRest),
            })
          }
        />
      </div>
      <div className='flex w-9 shrink-0 grow-0 items-center justify-center gap-1'>
        <button
          type='button'
          className='inline-flex h-8 w-8 items-center justify-center text-red-500 hover:text-red-400'
          onClick={() => removeDraftRow(index)}
          aria-label='Delete row'
          title='Delete'
        >
          <FiTrash2 size={15} />
        </button>
      </div>
    </div>
  );
}

export default function StrengthWorkoutComposer({
  value,
  onChange,
  showToolbar = true,
  mode,
  onModeChange,
}: {
  value: string[];
  onChange: (next: string[]) => void;
  showToolbar?: boolean;
  mode?: 'text' | 'form';
  onModeChange?: (next: 'text' | 'form') => void;
}) {
  const [internalMode, setInternalMode] = useState<'text' | 'form'>('form');
  const textAreaRef = useRef<HTMLTextAreaElement | null>(null);
  const [text, setText] = useState(() =>
    value.length > 0 ? value.join('\n') : createDefaultText()
  );
  const [rows, setRows] = useState<StrengthRow[]>(() => {
    return createRowDrafts(value.join('\n'));
  });
  const [draftRows, setDraftRows] = useState<StrengthRow[]>(() => {
    return createRowDrafts(value.join('\n'));
  });
  const [draftDirty, setDraftDirty] = useState<boolean[]>(() => {
    const parsed = parseRows(value.join('\n'));
    return parsed.length > 0 ? parsed.map(() => false) : [false];
  });
  const lastEmittedValue = useRef('');
  const lastValueRef = useRef(value.join('\n'));
  const categories = useMemo(
    () => getStrengthCategories().map((category) => category.value),
    []
  );
  const activeMode = mode ?? internalMode;

  // dnd-kit sensors
  const sensors = useSensors(useSensor(PointerSensor));

  useEffect(() => {
    const incoming = value.join('\n');
    if (incoming === lastValueRef.current) return;
    lastValueRef.current = incoming;
    const nextText = value.length > 0 ? value.join('\n') : createDefaultText();
    const nextRows = createRowDrafts(nextText);
    const nextDirty = nextRows.map(() => false);

    const frame = requestAnimationFrame(() => {
      setText(nextText);
      setRows(nextRows);
      setDraftRows(nextRows);
      setDraftDirty(nextDirty);
    });

    return () => cancelAnimationFrame(frame);
  }, [value]);

  useEffect(() => {
    const textarea = textAreaRef.current;
    if (!textarea || activeMode !== 'text') return;

    textarea.style.height = 'auto';
    textarea.style.overflowY = 'hidden';
    textarea.style.height = `${textarea.scrollHeight}px`;
  }, [activeMode, text]);

  useEffect(() => {
    const next = activeMode === 'text' ? text : renderRows(draftRows);
    const serialized = JSON.stringify(
      next
        .split('\n')
        .map((line) => line.trim())
        .filter(Boolean)
    );
    if (serialized === lastEmittedValue.current) return;
    lastEmittedValue.current = serialized;
    onChange(
      next
        .split('\n')
        .map((line) => line.trim())
        .filter(Boolean)
    );
  }, [activeMode, text, draftRows, onChange]);

  const updateText = (nextText: string) => {
    setText(nextText);
    setRows((current) => {
      const parsed = parseRows(nextText);
      // Ensure each row has a unique ID when parsing from text
      return parsed.length > 0
        ? parsed.map((row, index) => ({ ...row, id: `row-${index}` }))
        : current;
    });
  };

  const updateRow = (index: number, patch: Partial<StrengthRow>) => {
    if (patch.weightDisplayUnit) {
      writeStrengthWeightUnitCookie(patch.weightDisplayUnit);
    }
    setDraftRows((current) =>
      current.map((row, rowIndex) =>
        rowIndex === index ? { ...row, ...patch } : row
      )
    );
    setDraftDirty((current) =>
      current.map((dirty, rowIndex) => (rowIndex === index ? true : dirty))
    );
  };

  const addDraftRow = () => {
    const newId = `row-${draftRows.length}`;
    setDraftRows((current) => [
      ...current,
      {
        ...DEFAULT_ROW_BASE,
        weightDisplayUnit: getInitialStrengthWeightUnit(),
        id: newId,
      },
    ]);
    setDraftDirty((current) => [...current, false]);
  };

  const removeDraftRow = (index: number) => {
    setDraftRows((current) =>
      current.length > 1
        ? current.filter((_, rowIndex) => rowIndex !== index)
        : current
    );
    setDraftDirty((current) =>
      current.length > 1
        ? current.filter((_, rowIndex) => rowIndex !== index)
        : current
    );
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (active.id !== over?.id) {
      setDraftRows((currentRows) => {
        const oldIndex = currentRows.findIndex((row) => row.id === active.id);
        const newIndex = currentRows.findIndex((row) => row.id === over?.id);

        if (oldIndex === -1 || newIndex === -1) return currentRows;

        const updatedRows = arrayMove(currentRows, oldIndex, newIndex);
        // Update IDs after reordering to maintain uniqueness if needed, though existing IDs should be fine.
        // For safety, we can re-map IDs if they are generated sequentially.
        return updatedRows.map((row, index) => ({
          ...row,
          id: `row-${index}`,
        }));
      });
      setDraftDirty((current) => {
        return current.map(() => true);
      });
    }
  };

  return (
    <div className=''>
      {showToolbar && (
        <div className='flex items-center justify-end gap-2'>
          <button
            type='button'
            className='rounded-full border border-current px-2 text-[8px] font-bold uppercase tracking-wider leading-none box-border text-[#5f6875] transition-colors hover:bg-white/20 dark:text-white'
            onClick={addDraftRow}
            aria-label='Add new step'
            title='Add'
          >
            <FiPlus size={9} />
            <span className='sr-only sm:not-sr-only text-[7px] leading-none'>
              Add Step
            </span>
          </button>
          <SegmentedToggle
            value={activeMode}
            options={[
              { value: 'text', label: 'Text' },
              { value: 'form', label: 'Form' },
            ]}
            onChange={(next) => {
              if (onModeChange) onModeChange(next);
              else setInternalMode(next);
            }}
            size='xs'
          />
        </div>
      )}

      <div className='overflow-x-auto'>
        {activeMode === 'text' ? (
          <div className='flex flex-col gap-3'>
            <textarea
              ref={textAreaRef}
              id='strength-steps'
              className='workout-board w-full min-h-[150px] overflow-hidden p-3 text-sm font-sans resize-none'
              value={text}
              onChange={(e) => {
                updateText(e.target.value);
                const textarea = textAreaRef.current;
                if (!textarea) return;
                textarea.style.height = 'auto';
                textarea.style.height = `${textarea.scrollHeight}px`;
              }}
              placeholder={createDefaultText().replace('1x(', '4x(')}
            />
          </div>
        ) : (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={draftRows.map((row) => row.id)} // Pass IDs to SortableContext
              strategy={verticalListSortingStrategy}
            >
              <div className='workout-board flex min-h-[220px] flex-col gap-2 rounded-lg border-none bg-stone-300 p-3 dark:bg-stone-700 text-gray-800 dark:text-white overflow-x-auto'>
                <div className='flex flex-nowrap items-center justify-between gap-1 px-0 text-sm font-semibold min-w-full'>
                  <div className='flex w-8 shrink-0 grow-0 flex-col gap-1'>
                    <span></span> {/* Placeholder for drag handle */}
                  </div>
                  <div className='flex w-20 shrink-0 grow-0 flex-col gap-1'>
                    <span>Sets</span>
                  </div>
                  <div className='flex min-w-0 flex-1 flex-col gap-1'>
                    <span>Category</span>
                  </div>
                  <div className='flex min-w-0 flex-1 flex-col gap-1'>
                    <span>Exercise</span>
                  </div>
                  <div className='flex w-20 shrink-0 grow-0 flex-col gap-1'>
                    <span>Reps</span>
                  </div>
                  <div className='flex w-20 shrink-0 grow-0 flex-col gap-1'>
                    <span>Weight</span>
                  </div>
                  <div className='flex w-24 shrink-0 grow-0 flex-col gap-1'>
                    <span>Unit</span>
                  </div>
                  <div className='flex w-20 shrink-0 grow-0 flex-col gap-1'>
                    <span>Rest</span>
                  </div>
                  <div className='flex w-9 shrink-0 grow-0 flex-col gap-1'>
                    <span className='sr-only'>Delete</span>
                  </div>
                </div>

                <div className='pr-1'>
                  <div className='space-y-2'>
                    {draftRows.map((row, index) => (
                      <SortableRow
                        key={row.id}
                        row={row}
                        index={index}
                        updateRow={updateRow}
                        removeDraftRow={removeDraftRow}
                        categories={categories}
                        isDragging={false}
                      />
                    ))}
                  </div>
                </div>
              </div>
            </SortableContext>
          </DndContext>
        )}
      </div>
    </div>
  );
}
